import { useEffect, useState, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../../../utils/axiosInstance";
import PaymentMethodForm from "./PaymentMethodForm";
import PaymentMethodList from "./PaymentMethodList";
import ConfirmModal from "../../../../../components/Modals/ConfirmModal";
import { AuthContext } from "../../../../../context/AuthContext";
import { isProjectManager } from "../../../../../utils/constants/accountType";

const PaymentMethodsPage = ({ vendorId }) => {
  const { user } = useContext(AuthContext);
  const readOnly = isProjectManager(user);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [methodToDelete, setMethodToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const action = searchParams.get("action");
  const editId = searchParams.get("id");

  useEffect(() => {
    if (vendorId) fetchMethods();
  }, [vendorId]);

  const fetchMethods = async () => {
    try {
      const res = await api.get(`/vendor-payment-methods/${vendorId}`);
      setMethods(res.data.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch vendor payment methods");
    } finally {
      setLoading(false);
    }
  };

  const setAction = (nextAction, id) => {
    const next = new URLSearchParams(searchParams);
    if (nextAction) {
      next.set("action", nextAction);
    } else {
      next.delete("action");
    }
    if (id !== undefined && id !== null) {
      next.set("id", String(id));
    } else {
      next.delete("id");
    }
    setSearchParams(next, { replace: true });
  };

  const handleSave = async (formData) => {
    try {
      const payload = {
        ...formData,
        vendor_id: vendorId,
      };

      if (action === "edit" && editId) {
        await api.put(`/vendor-payment-methods/${editId}`, payload);
        await fetchMethods();
      } else {
        const res = await api.post(`/vendor-payment-methods`, payload);
        setMethods((prev) => [...prev, res.data.data]);
      }

      setAction(null);
      setDeleteError("");
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const handleDeleteClick = (id) => {
    const methodToRemove = methods.find((m) => m.id === id);

    if (methodToRemove?.is_default && methods.length > 1) {
      setDeleteError(
        "Cannot delete default payment method. Please set another payment method as default first."
      );
      return;
    }

    setMethodToDelete(id);
    setDeleteError("");
  };

  const handleDelete = async () => {
    if (!methodToDelete) return;
    try {
      await api.delete(`/vendor-payment-methods/${methodToDelete}`);
      setMethods((prev) => prev.filter((m) => m.id !== methodToDelete));
      setMethodToDelete(null);
      setDeleteError("");
    } catch (err) {
      console.error(err);
      const errorMsg =
        err.response?.data?.message || "Failed to delete vendor payment method";
      setDeleteError(errorMsg);
    }
  };

  if (loading)
    return (
      <div className="text-center mt-20 text-gray-500 text-sm">Loading...</div>
    );

  if (action === "add" || (action === "edit" && editId)) {
    let methodToEdit = null;
    if (action === "edit" && editId) {
      methodToEdit = methods.find((m) => String(m.id) === String(editId));
      if (!methodToEdit) {
        return (
          <div className="text-center mt-10 text-gray-500">
            Payment method not found.
          </div>
        );
      }
    }

    return (
      <PaymentMethodForm
        methodToEdit={methodToEdit}
        onSave={handleSave}
        onCancel={() => {
          setAction(null);
          setDeleteError("");
        }}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Vendor Payment Methods
        </h3>

        {!readOnly && (
          <button
            onClick={() => {
              setAction("add");
              setDeleteError("");
            }}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
          >
            Add Payment Method
          </button>
        )}
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}
      {deleteError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 flex items-start justify-between">
          <span>{deleteError}</span>
          <button
            onClick={() => setDeleteError("")}
            className="text-red-900 hover:text-red-700 ml-4"
          >
            ×
          </button>
        </div>
      )}

      <PaymentMethodList
        methods={methods}
        readOnly={readOnly}
        onEdit={(method) => {
          setAction("edit", method.id);
          setDeleteError("");
        }}
        onDelete={handleDeleteClick}
      />

      {methodToDelete && (
        <ConfirmModal
          title="Confirm Deletion"
          message="Are you sure you want to delete this vendor payment method?"
          onCancel={() => {
            setMethodToDelete(null);
            setDeleteError("");
          }}
          onConfirm={handleDelete}
          confirmText="Delete"
          confirmColor="bg-red-600"
          confirmHoverColor="hover:bg-red-700"
        />
      )}
    </div>
  );
};

export default PaymentMethodsPage;
